import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Save, X, DollarSign, FileText } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import paymentsService from '../services/paymentsService';
import invoicesService from '../services/invoicesService';
import { toLastFirst, byLastName } from '../utils/customerName';

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

// Given open invoices (already oldest-due-first) and an amount, return the
// set of invoice ids to pre-check so the payment covers them oldest-first.
// We greedily select whole invoices whose cumulative `remaining` is covered
// by the amount, plus the next one the payment partially covers — mirrors
// the backend's oldest-first applier so the pre-check matches what would
// happen on submit.
const autoSelectOldestFirst = (openInvoices, amount) => {
  const picked = new Set();
  let left = Number(amount) || 0;
  if (left <= 0) return picked;
  for (const inv of openInvoices) {
    if (left <= 0) break;
    const remaining = Number(inv.remaining) || 0;
    if (remaining <= 0) continue;
    picked.add(inv._id);
    left -= remaining;
  }
  return picked;
};

const PaymentForm = () => {
  const { facilityId } = useFacility();
  const navigate = useNavigate();
  const { id: paymentId } = useParams();
  const isEdit = Boolean(paymentId);
  const [readOnlyStripe, setReadOnlyStripe] = useState(false);
  // Customer-page "Record Payment" passes ?customer_id=... so the form
  // lands pre-scoped to that tenant. When present, we also auto-pick
  // their active rental + balance once rentals load (see fetchData
  // below).
  const [searchParams] = useSearchParams();
  const prefillCustomerId = searchParams.get('customer_id') || '';
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [selectedCustomerRentals, setSelectedCustomerRentals] = useState([]);
  // Invoice-matching: the selected customer's OPEN invoices + which ones the
  // operator has checked to close with this payment. `autoApply` true means
  // we keep the selection synced to oldest-first coverage of the amount; the
  // moment the operator toggles a checkbox, it flips to manual (full
  // override) and we stop auto-recomputing.
  const [openInvoices, setOpenInvoices] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [autoApply, setAutoApply] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: prefillCustomerId,
    rental_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    if (!facilityId) return;
    try {
      const [customersRes, rentalsRes] = await Promise.all([
        // status=all so the customer dropdown isn't limited to the endpoint's
        // default "active" set (customers with a non-Ended rental). A payment
        // can be recorded for any non-archived customer — mirrors RentalForm.
        // Trailing slashes — no-slash draws a 307 and Safari strips the
        // Authorization header on redirects (2026-08-02).
        apiClient.get(`/api/facilities/${facilityId}/customers/?status=all`),
        apiClient.get(`/api/facilities/${facilityId}/rentals/`)
      ]);
      setCustomers(customersRes.data);
      const activeRentals = rentalsRes.data.filter(r => r.status === 'Active');
      setRentals(activeRentals);
      // If we arrived with ?customer_id=..., pre-pick the customer's
      // active rental and balance to mirror the manual onChange path.
      if (prefillCustomerId) {
        const theirs = activeRentals.filter(r => r.customer_id === prefillCustomerId);
        setSelectedCustomerRentals(theirs);
        if (theirs.length === 1) {
          setFormData(prev => ({
            ...prev,
            rental_id: theirs[0]._id,
            amount: theirs[0].balance > 0 ? theirs[0].balance : theirs[0].monthly_rate,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [facilityId, prefillCustomerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Edit mode: load the existing payment and prefill. Stripe-originated
  // payments are read-only (the real charge lives on Stripe).
  useEffect(() => {
    if (!facilityId || !isEdit) return;
    (async () => {
      try {
        const { data } = await paymentsService.getById(facilityId, paymentId);
        if (data.stripe_payment_id) setReadOnlyStripe(true);
        setFormData({
          customer_id: data.customer_id || '',
          rental_id: data.rental_id || '',
          amount: data.amount ?? '',
          payment_date: (data.payment_date || '').split('T')[0],
          payment_method: data.payment_method || 'Cash',
          notes: data.notes || '',
        });
      } catch (err) {
        alert('Could not load this payment.');
      }
    })();
  }, [facilityId, isEdit, paymentId]);

  // Invoice-matching is a record-time concern, so we only load the open-
  // invoice checklist in CREATE mode. Refetch whenever the customer or the
  // selected rental changes; scope to the rental when one is picked (matches
  // the backend's oldest-first applier scope), else all the customer's open
  // invoices. Loading resets to auto-apply mode.
  useEffect(() => {
    if (!facilityId || isEdit || !formData.customer_id) {
      setOpenInvoices([]);
      setSelectedInvoiceIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      setInvoicesLoading(true);
      try {
        const { data } = await invoicesService.getOpen(
          facilityId, formData.customer_id, formData.rental_id || undefined,
        );
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setOpenInvoices(list);
        setAutoApply(true);
        setSelectedInvoiceIds(autoSelectOldestFirst(list, formData.amount));
      } catch (err) {
        if (!cancelled) {
          setOpenInvoices([]);
          setSelectedInvoiceIds(new Set());
        }
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, isEdit, formData.customer_id, formData.rental_id]);

  // While in auto mode, keep the pre-check synced to the entered amount so
  // the operator sees the oldest-first selection grow/shrink as they type.
  // Manual mode (operator toggled a box) freezes the selection.
  useEffect(() => {
    if (isEdit || !autoApply) return;
    setSelectedInvoiceIds(autoSelectOldestFirst(openInvoices, formData.amount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.amount, openInvoices, autoApply, isEdit]);

  // Live tally of what the checked invoices consume vs. the entered amount.
  const tally = useMemo(() => {
    const amount = Number(formData.amount) || 0;
    const dueOnSelected = openInvoices.reduce(
      (sum, inv) => selectedInvoiceIds.has(inv._id)
        ? sum + (Number(inv.remaining) || 0)
        : sum,
      0,
    );
    const applying = Math.min(amount, dueOnSelected);
    const leftOver = Math.max(amount - dueOnSelected, 0);
    const stillDue = Math.max(dueOnSelected - amount, 0);
    return { applying, leftOver, stillDue };
  }, [formData.amount, openInvoices, selectedInvoiceIds]);

  const toggleInvoice = (invoiceId) => {
    // First manual toggle takes over from auto mode (full override).
    setAutoApply(false);
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'customer_id' && value) {
      const customerRentals = rentals.filter(r => r.customer_id === value);
      setSelectedCustomerRentals(customerRentals);
      if (customerRentals.length === 1) {
        setFormData(prev => ({
          ...prev,
          rental_id: customerRentals[0]._id,
          amount: customerRentals[0].balance > 0 ? customerRentals[0].balance : customerRentals[0].monthly_rate
        }));
      }
    }

    if (name === 'rental_id' && value) {
      const selectedRental = rentals.find(r => r._id === value);
      if (selectedRental) {
        setFormData(prev => ({
          ...prev,
          amount: selectedRental.balance > 0 ? selectedRental.balance : selectedRental.monthly_rate
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        await paymentsService.update(facilityId, paymentId, {
          amount: parseFloat(formData.amount),
          payment_method: formData.payment_method,
          payment_date: formData.payment_date,
          notes: formData.notes || null,
          rental_id: formData.rental_id || null,
        });
      } else {
        // Send the checked invoices in their displayed (oldest-due-first)
        // order so the backend closes exactly those. When the customer has
        // open invoices but nothing is checked, send an empty list to opt
        // OUT of touching invoices. When there are no open invoices at all,
        // omit the field entirely so the backend keeps its default
        // auto-apply behavior (backward compatible).
        const payload = {
          ...formData,
          amount: parseFloat(formData.amount),
        };
        if (openInvoices.length > 0) {
          payload.apply_to_invoice_ids = openInvoices
            .filter((inv) => selectedInvoiceIds.has(inv._id))
            .map((inv) => inv._id);
        }
        await apiClient.post(`/api/facilities/${facilityId}/payments/`, payload);
      }
      navigate('/payments');
    } catch (error) {
      console.error('Error saving payment:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e) => e.msg || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : (isEdit ? 'Failed to update payment' : 'Failed to record payment'));
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all";
  const labelClass = "block text-[13px] font-medium text-slate-700 mb-1.5";

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">
          {isEdit ? 'Edit Payment' : 'Record Payment'}
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          {isEdit
            ? 'Editing reverses the original and reapplies it; the rental balance updates automatically.'
            : 'Record a new payment transaction'}
        </p>
      </div>

      {readOnlyStripe && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          This is a Stripe-processed payment and can’t be edited here — the charge lives on
          Stripe. Issue a refund in Stripe, then record a correction if needed.
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className={labelClass}>Customer *</label>
            {/* Locked when launched from a customer page (preset via
                ?customer_id=) or when editing — the tenant is fixed. */}
            <select name="customer_id" required disabled={isEdit || Boolean(prefillCustomerId)} value={formData.customer_id} onChange={handleChange} className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-500`}>
              <option value="">Select a customer</option>
              {[...customers]
                .sort(byLastName)
                .map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {toLastFirst(customer.full_name)}{customer.email ? ` - ${customer.email}` : ''}
                  </option>
                ))}
            </select>
            {!isEdit && prefillCustomerId && (
              <p className="text-[11px] text-slate-400 mt-1">Recording a payment for this customer.</p>
            )}
          </div>

          {formData.customer_id && (
            <div>
              <label className={labelClass}>Rental (Optional - for applying to balance)</label>
              <select name="rental_id" value={formData.rental_id} onChange={handleChange} className={inputClass}>
                <option value="">General Payment</option>
                {selectedCustomerRentals.map((rental) => (
                  <option key={rental._id} value={rental._id}>
                    Unit {rental.unit_number} - Balance: ${rental.balance.toFixed(2)} - Rate: ${rental.monthly_rate}/mo
                  </option>
                ))}
              </select>
              {selectedCustomerRentals.length === 0 && (
                <p className="text-[11px] text-slate-400 mt-1">This customer has no active rentals</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Amount *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input type="number" name="amount" required step="0.01" min="0.01" value={formData.amount} onChange={handleChange} className={`${inputClass} pl-8`} placeholder="0.00" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Payment Date *</label>
              <input type="date" name="payment_date" required value={formData.payment_date} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          {/* Invoice matching — operator picks exactly which open invoices
              this payment closes. Defaults to oldest-first coverage of the
              amount (pre-checked); any toggle is a full override. CREATE
              mode only. */}
          {!isEdit && formData.customer_id && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[13px] font-medium text-slate-700">Apply to invoices</label>
                {openInvoices.length > 0 && (
                  <span className="text-[11px] text-slate-400">
                    {autoApply ? 'Auto — oldest first' : 'Manual selection'}
                  </span>
                )}
              </div>

              {invoicesLoading ? (
                <p className="text-[12px] text-slate-400">Loading open invoices…</p>
              ) : openInvoices.length === 0 ? (
                <p className="text-[12px] text-slate-400">
                  No open invoices for this customer{formData.rental_id ? ' on this rental' : ''}.
                  The payment will reduce the rental balance.
                </p>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                    {openInvoices.map((inv) => {
                      const checked = selectedInvoiceIds.has(inv._id);
                      return (
                        <label
                          key={inv._id}
                          className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-blue-50/60' : 'hover:bg-slate-50'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInvoice(inv._id)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500/30"
                          />
                          <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-medium text-slate-800 truncate">
                              {inv.invoice_number || 'Invoice'}
                              {inv.due_date ? <span className="text-slate-400 font-normal"> · due {inv.due_date}</span> : null}
                            </div>
                            {inv.notes ? (
                              <div className="text-[11px] text-slate-400 truncate">{inv.notes}</div>
                            ) : null}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[13px] font-medium text-slate-800">{money(inv.remaining)}</div>
                            <div className="text-[11px] text-slate-400">of {money(inv.total)}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Live tally */}
                  <p className="text-[12px] text-slate-500 mt-2">
                    Applying <span className="font-semibold text-slate-700">{money(tally.applying)}</span> of {money(formData.amount)}
                    {' · '}
                    <span className="font-semibold text-slate-700">{money(tally.leftOver)}</span> left over
                    {' / '}
                    <span className="font-semibold text-slate-700">{money(tally.stillDue)}</span> still due
                  </p>
                </>
              )}
            </div>
          )}

          <div>
            <label className={labelClass}>Payment Method *</label>
            <select name="payment_method" required value={formData.payment_method} onChange={handleChange} className={inputClass}>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Money Order">Money Order</option>
              <option value="Credit Card">Credit Card</option>
              <option value="ACH">ACH</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" className={inputClass} placeholder="Check number, transaction ID, or any additional notes..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate('/payments')} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button type="submit" disabled={loading || readOnlyStripe} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
              <Save className="h-3.5 w-3.5" /> {loading ? 'Saving...' : (isEdit ? 'Save changes' : 'Record Payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
